/**
 * emaMath.js
 *
 * Exponential Moving Average (EMA) calculation + 3-way cross detection
 * for the Liquidity Map. 100% pure functions — no I/O, no React.
 *
 * Three crosses are detected, named after the EMA pair to make the
 * output unambiguous (no generic "bull_cross"):
 *
 *   • detectGoldenDeathCross(candles, coin, timeframe)
 *       → pattern: "golden_cross" | "death_cross"     (EMA50 × EMA200)
 *
 *   • detectEMA21_50Cross(candles, coin, timeframe)
 *       → pattern: "ema_21_50_bull" | "ema_21_50_bear"
 *
 *   • detectEMA9_21Cross(candles, coin, timeframe)
 *       → pattern: "ema_9_21_bull"  | "ema_9_21_bear"
 *
 * Output shape mirrors chartPatterns.js exactly so the same rendering
 * code path in HeatmapCanvas.jsx (status-aware colors/dashes) can
 * consume it without a new dispatch:
 *
 *   {
 *     pattern: <string> | null,
 *     status:  "confirmed" | "forming" | "invalidated" | null,
 *     ts_cross:    <epoch ms> | null,
 *     idx_cross:   <candle index> | null,
 *     fast_value:  <number> | null,   // value of the FAST EMA at cross
 *     slow_value:  <number> | null,   // value of the SLOW EMA at cross
 *     separation_pct: <number>,       // |fast - slow| / slow * 100
 *     reason: <string>,               // when pattern is null
 *   }
 *
 * Timeframe-aware: each cross has a minimum relevant timeframe. Below
 * it the detector returns { pattern:null, reason:"low_timeframe" } —
 * the math is the same but the noise floor swamps any signal. This
 * matches trader convention (golden cross on 15m is meaningless).
 */

// ─────────────────────────────────────────────────────────────────────
// EMA core — standard formula with SMA seed for the first `period`
// values, then exponential recurrence onward. Output array has the
// same length as input; the first `period - 1` slots are NaN so any
// consumer aware of the seed offset can skip them deterministically.
// ─────────────────────────────────────────────────────────────────────
export function computeEMA(closes, period) {
  const n = Array.isArray(closes) ? closes.length : 0;
  const out = new Array(n).fill(NaN);
  if (n < period || period <= 0) return out;
  // Seed: simple average of the first `period` closes.
  let sum = 0;
  for (let i = 0; i < period; i++) sum += closes[i];
  let prev = sum / period;
  out[period - 1] = prev;
  // EMA recurrence: EMA[i] = close[i] * α + EMA[i-1] * (1 - α)
  const alpha = 2 / (period + 1);
  for (let i = period; i < n; i++) {
    prev = closes[i] * alpha + prev * (1 - alpha);
    out[i] = prev;
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────
// Timeframe relevance table. A cross is only emitted if the current
// timeframe is in the allow-list for that pair. Below it the detector
// returns null pattern with reason="low_timeframe" so the UI knows
// the math ran but the signal was suppressed for noise reasons.
//
// Convention sources: institutional trading desks publish golden cross
// signals on 4h+ only; swing traders use 21×50 from 1h+; scalpers use
// 9×21 on any chart down to 15m. Below 15m we don't trade EMA crosses
// at all on this product.
// ─────────────────────────────────────────────────────────────────────
const TF_RELEVANCE = {
  ema_9_21:   new Set(["15m", "1h", "4h", "1d", "1w"]),
  ema_21_50:  new Set(["1h", "4h", "1d", "1w"]),
  ema_50_200: new Set(["4h", "1d", "1w"]),
};

// ─────────────────────────────────────────────────────────────────────
// Per-pair "forming" separation thresholds (% distance between the
// two EMAs at which we declare imminent cross). Lower band-pair = more
// volatile baseline ⇒ tighter threshold. NOT calibrated from samples
// (would need its own .calibrate.mjs run); pre-set from
// trader-convention round numbers and easy to tune later.
//
// We accept the lack of calibration here because:
//   1. "Forming" is a soft state — being wrong by ±0.2% only changes
//      whether the same near-touching EMAs draw dashed vs solid.
//   2. Crosses themselves are deterministic (sign flip of fast-slow);
//      forming is only for UI prominence.
// ─────────────────────────────────────────────────────────────────────
const FORMING_SEPARATION_PCT = {
  ema_9_21:   0.50,
  ema_21_50:  1.00,
  ema_50_200: 2.00,
};

// How many candles back we look for a recent cross before declaring
// "no_recent_cross". Roughly 5 trading days at 1h, 2 weeks at 4h,
// 50 days at 1d — wide enough to catch any cross still actively
// driving price action.
const RECENT_CROSS_K = 120;

// ─────────────────────────────────────────────────────────────────────
// Internal: find the most recent index `i` where sign(fast - slow)
// flipped relative to `i-1`. Returns null if no cross within the last
// `RECENT_CROSS_K` candles. Direction is encoded as +1 (fast went
// above slow) / -1 (fast went below slow).
// ─────────────────────────────────────────────────────────────────────
function _findLastCross(fastArr, slowArr) {
  const n = Math.min(fastArr.length, slowArr.length);
  const start = Math.max(1, n - RECENT_CROSS_K);
  for (let i = n - 1; i >= start; i--) {
    const fA = fastArr[i], sA = slowArr[i];
    const fB = fastArr[i - 1], sB = slowArr[i - 1];
    if (!Number.isFinite(fA) || !Number.isFinite(sA) ||
        !Number.isFinite(fB) || !Number.isFinite(sB)) continue;
    const cur = fA - sA;
    const prev = fB - sB;
    // Strict sign flip excluding zero-crossing-from-zero (degenerate).
    if (cur === 0 || prev === 0) continue;
    if ((cur > 0) !== (prev > 0)) {
      return { idx: i, dir: cur > 0 ? 1 : -1 };
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────
// Internal: classify the current state given the most-recent cross
// (or absence thereof). Encapsulates the 3-state machine used by all
// three pair detectors so the rules stay aligned.
//
//   confirmed     → cross within last RECENT_CROSS_K candles, the
//                   "winning" EMA still on the dominant side at HEAD
//                   AND price hasn't re-crossed the winning EMA.
//   invalidated   → cross within window, but price has since closed
//                   on the WRONG side of the winning EMA (signal
//                   failed).
//   forming       → no cross, but |fast - slow| / slow * 100 < threshold
//                   ⇒ the two EMAs are converging.
//   null pattern  → no cross, no near convergence ⇒ no actionable
//                   pattern; reason="no_recent_cross".
// ─────────────────────────────────────────────────────────────────────
function _classify(fastArr, slowArr, candles, sepThreshold) {
  const n = Math.min(fastArr.length, slowArr.length, candles.length);
  if (n < 2) return { state: null, reason: "not_enough_data" };
  const headFast = fastArr[n - 1];
  const headSlow = slowArr[n - 1];
  if (!Number.isFinite(headFast) || !Number.isFinite(headSlow)) {
    return { state: null, reason: "ema_not_seeded" };
  }
  const headSep = Math.abs(headFast - headSlow) / Math.abs(headSlow) * 100;
  const cross = _findLastCross(fastArr, slowArr);

  if (cross) {
    const headDir = headFast - headSlow > 0 ? 1 : -1;
    // Re-cross check: after the cross, the price (close) should stay
    // on the winning EMA's side. If price closes back on the LOSING
    // side, the signal is invalidated.
    const winningEMA = cross.dir > 0 ? fastArr : slowArr;
    let invalidated = false;
    for (let j = cross.idx + 1; j < n; j++) {
      const px = candles[j]?.c;
      const ema = winningEMA[j];
      if (!Number.isFinite(px) || !Number.isFinite(ema)) continue;
      // bull cross: price should stay ABOVE winning (fast) EMA.
      // bear cross: price should stay BELOW winning (slow) EMA.
      if (cross.dir > 0 && px < ema) { invalidated = true; break; }
      if (cross.dir < 0 && px > ema) { invalidated = true; break; }
    }
    // Also invalidated if the head direction flipped vs cross direction
    // (a counter-cross happened but wasn't caught as a fresh cross —
    // shouldn't happen in practice because _findLastCross returns the
    // newest, but kept as belt-and-suspenders).
    if (headDir !== cross.dir) invalidated = true;

    return {
      state: invalidated ? "invalidated" : "confirmed",
      cross,
      headSep,
    };
  }

  // No recent cross: check if forming (imminent convergence).
  if (headSep < sepThreshold) {
    return { state: "forming", cross: null, headSep };
  }
  return { state: null, reason: "no_recent_cross", headSep };
}

// ─────────────────────────────────────────────────────────────────────
// Public detector factory. Each pair detector is a thin wrapper
// around this so adding a new pair (if ever needed) is one line.
// ─────────────────────────────────────────────────────────────────────
function _detectPair(candles, coin, timeframe, fastPeriod, slowPeriod, pairKey, bullName, bearName) {
  void coin; // per-coin calibration not used (yet) — keep signature stable.
  if (!Array.isArray(candles) || candles.length < slowPeriod + 2) {
    return { pattern: null, status: null, reason: "not_enough_candles",
      ts_cross: null, idx_cross: null, fast_value: null, slow_value: null, separation_pct: 0 };
  }
  const tfAllowed = TF_RELEVANCE[pairKey];
  if (tfAllowed && !tfAllowed.has(timeframe)) {
    return { pattern: null, status: null, reason: "low_timeframe",
      ts_cross: null, idx_cross: null, fast_value: null, slow_value: null, separation_pct: 0 };
  }
  const closes = candles.map((c) => c.c);
  const fastArr = computeEMA(closes, fastPeriod);
  const slowArr = computeEMA(closes, slowPeriod);
  const sepThreshold = FORMING_SEPARATION_PCT[pairKey] ?? 1.0;
  const cls = _classify(fastArr, slowArr, candles, sepThreshold);

  if (cls.state === null) {
    return { pattern: null, status: null, reason: cls.reason || "no_pattern",
      ts_cross: null, idx_cross: null, fast_value: null, slow_value: null,
      separation_pct: cls.headSep ?? 0 };
  }
  // Determine direction. If we have a cross, use cross.dir. If forming,
  // pick the side the fast EMA is currently approaching from (a
  // "forming bull" means fast is below slow and rising toward it).
  let dir;
  if (cls.cross) {
    dir = cls.cross.dir;
  } else {
    // Forming: emit the direction that WOULD result if the convergence
    // completes — i.e., where the fast EMA is heading. Use the slope
    // of fast over the last 5 candles as proxy.
    const n = fastArr.length;
    const back = Math.max(0, n - 6);
    const slope = (fastArr[n - 1] - fastArr[back]);
    dir = slope >= 0 ? 1 : -1;
  }
  const idx = cls.cross ? cls.cross.idx : (candles.length - 1);
  return {
    pattern: dir > 0 ? bullName : bearName,
    status: cls.state,
    ts_cross: candles[idx]?.t ?? null,
    idx_cross: idx,
    fast_value: fastArr[idx] ?? null,
    slow_value: slowArr[idx] ?? null,
    separation_pct: cls.headSep ?? 0,
  };
}

export function detectEMA9_21Cross(candles, coin, timeframe) {
  return _detectPair(candles, coin, timeframe, 9, 21, "ema_9_21",
    "ema_9_21_bull", "ema_9_21_bear");
}

export function detectEMA21_50Cross(candles, coin, timeframe) {
  return _detectPair(candles, coin, timeframe, 21, 50, "ema_21_50",
    "ema_21_50_bull", "ema_21_50_bear");
}

export function detectGoldenDeathCross(candles, coin, timeframe) {
  return _detectPair(candles, coin, timeframe, 50, 200, "ema_50_200",
    "golden_cross", "death_cross");
}

// Convenience: run all 3 detectors at once and return as a list,
// filtering out null patterns. Order is fast → slow so the UI can
// stack labels with the most-actionable on top.
export function detectAllEMACrosses(candles, coin, timeframe) {
  const out = [
    detectEMA9_21Cross(candles, coin, timeframe),
    detectEMA21_50Cross(candles, coin, timeframe),
    detectGoldenDeathCross(candles, coin, timeframe),
  ];
  return out.filter((r) => r.pattern !== null);
}

// Re-exported for tests / debugging.
export const __internals = {
  TF_RELEVANCE,
  FORMING_SEPARATION_PCT,
  RECENT_CROSS_K,
  _findLastCross,
  _classify,
};
